import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

const FRAME_STYLES = {
  bronze: {
    border: '2.5px solid #CD7F32',
    boxShadow: '0 0 10px rgba(205, 127, 50, 0.5), 0 0 20px rgba(205, 127, 50, 0.2)',
    background: 'linear-gradient(135deg, #CD7F32 0%, #A0522D 50%, #CD7F32 100%)',
  },
  silver: {
    border: '2.5px solid #A8A8A8',
    boxShadow: '0 0 10px rgba(160, 160, 160, 0.6), 0 0 20px rgba(192, 192, 192, 0.3)',
    background: 'linear-gradient(135deg, #E8E8E8 0%, #A0A0A0 50%, #C8C8C8 100%)',
  },
  gold: {
    border: '2.5px solid #FFD700',
    boxShadow: '0 0 12px rgba(255, 215, 0, 0.6), 0 0 24px rgba(255, 165, 0, 0.3)',
    background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FFD700 100%)',
  },
  diamond: {
    border: '2.5px solid #00CED1',
    boxShadow: '0 0 14px rgba(0, 206, 209, 0.7), 0 0 28px rgba(65, 105, 225, 0.4)',
    background: 'linear-gradient(135deg, #B9F2FF 0%, #00CED1 33%, #4169E1 66%, #B9F2FF 100%)',
  },
};

const STAFF_ROLES = ['admin', 'major', 'community_manager'];

const ENGRAVING_CLASS = {
  plain: '',
  hairline: '',
  double: 'aura-markers',
  markers: 'aura-markers',
  guilloche: 'aura-guilloche',
  hallmark: 'aura-guilloche aura-hallmark',
};

const METAL_COLOR = { bronze: '#CD7F32', silver: '#A8A8A8', gold: '#C9A86A', diamond: '#7FD4DA' };

const FramedAvatar = ({
  src,
  fallback,
  frame,
  size = 'md',
  className = '',
  role,
  seniority,
  ...props
}) => {
  const isStaff = STAFF_ROLES.includes(role);
  const engraving = ENGRAVING_CLASS[seniority?.engraving] || '';
  const auraTitle = isStaff
    ? "Équipe Odys'Invest"
    : seniority?.active_months
      ? `Membre depuis ${seniority.active_months} mois actif${seniority.active_months > 1 ? 's' : ''} · ${seniority.label}`
      : undefined;
  const sizeClasses = {
    xs: 'h-6 w-6',
    sm: 'h-7 w-7',
    md: 'h-8 w-8',
    chat: 'h-9 w-9',
    lg: 'h-10 w-10',
    xl: 'h-16 w-16',
    profile: 'h-32 w-32',
  };

  const ringPadding = {
    xs: 1,
    sm: 1.5,
    md: 2,
    chat: 2,
    lg: 2,
    xl: 3,
    profile: 4,
  };

  const avatarSize = sizeClasses[size] || sizeClasses.md;
  const frameStyle = frame ? FRAME_STYLES[frame] : null;

  if (!frameStyle && !isStaff && !engraving) {
    return (
      <Avatar className={`${avatarSize} border border-border ${className}`} data-testid="frame-none" {...props}>
        <AvatarImage src={src} />
        <AvatarFallback className="text-xs bg-secondary">{fallback}</AvatarFallback>
      </Avatar>
    );
  }

  const pad = ringPadding[size] || 2;

  return (
    <div
      className={`relative inline-flex items-center justify-center self-start rounded-full flex-shrink-0 ${engraving} ${
        isStaff ? 'aura-staff' : frame === 'diamond' ? 'animate-shimmer' : ''
      }`}
      style={{
        padding: `${pad}px`,
        color: isStaff ? '#0F7B5A' : METAL_COLOR[frame] || '#C9A86A',
        background: isStaff ? 'linear-gradient(135deg, #0F7B5A 0%, #14A575 50%, #0F7B5A 100%)' : frameStyle?.background,
        boxShadow: isStaff
          ? '0 0 10px rgba(15, 123, 90, .55), 0 0 22px rgba(20, 165, 117, .28)'
          : frameStyle?.boxShadow,
      }}
      title={auraTitle}
      data-testid={isStaff ? 'frame-staff' : `frame-${frame || 'none'}`}
    >
      <Avatar className={`${avatarSize} border-0 ${className}`} {...props}>
        <AvatarImage src={src} />
        <AvatarFallback className="text-xs bg-secondary">{fallback}</AvatarFallback>
      </Avatar>
    </div>
  );
};

export default FramedAvatar;
